import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useTheme as useAppTheme } from '@src/theme/hooks/useTheme';
import { type ReactNode, useEffect } from 'react';

/**
 * ThemeProviderのProps型定義
 */
type ThemeProviderProps = {
  children: ReactNode;
};

/**
 * アプリケーション用のThemeProviderコンポーネント
 *
 * MUIテーマをアプリケーション全体に提供します。
 * 元のスタイリングを維持するため、CssBaselineは使用しません。
 *
 * @param {ThemeProviderProps} props - コンポーネントのプロパティ
 * @param {ReactNode} props.children - 子コンポーネント
 * @returns {ReactElement} ThemeProviderコンポーネント
 *
 * @example
 * ```tsx
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 * ```
 *
 * @since 1.0.0
 * @author Resume Commit Team
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const currentTheme = useAppTheme();

  useEffect(() => {
    const mode = currentTheme.palette?.mode ?? 'light';
    const backgroundColor = currentTheme.palette?.background?.default;
    const focusOutlineColor = currentTheme.palette?.primary?.main;

    // OSやアプリ設定に合わせてCSSカスタムプロパティを同期させる
    document.documentElement.dataset.colorMode = mode;
    document.documentElement.style.colorScheme = mode;

    if (backgroundColor) {
      document.documentElement.style.setProperty('--app-bg', backgroundColor);
      document.documentElement.style.backgroundColor = backgroundColor;
      document.body.style.backgroundColor = backgroundColor;
    }

    if (focusOutlineColor) {
      document.documentElement.style.setProperty('--focus-outline-color', focusOutlineColor);
    }
  }, [currentTheme]);

  return (
    <MuiThemeProvider theme={currentTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}
